import React, { useState } from 'react'
import AppLoginLogo from '@assets/images/meta-login-logo.svg'
import EditIcon from '@assets/icons/edit-pen-icon.svg'
import SMSIcon from '@assets/icons/sms-icon.svg'
import WhatsAppIcon from '@assets/icons/whatsapp-icon.svg'
import LeftArrow from '@assets/icons/line-arrow-left.svg';
import OpenEyeIcon from '@assets/icons/open-eye-icon.svg';
import ClosedEyeIcon from '@assets/icons/closed-eye-icon.svg';
import styles from './AdmissionNoLogin.module.css';
import { environment } from '@environments/environment'
import { motion } from "motion/react"
import {
    useLoginFlow,
    useVerifyAdmission,
    usePasswordLogin,
    useOtpLogin,
    useOtpInput,
    type LoginFlowState,
} from '@hooks/login'

function EnterAdmissionNo({
    admissionNo,
    setAdmissionNo,
    setMobileNumber,
    setLoginFlowState,
}: {
    admissionNo: string
    setAdmissionNo: (admissionNo: string) => void
    setMobileNumber: (mobileNumber: string) => void
    setLoginFlowState: (flow: LoginFlowState) => void
}) {
    const [isFocused, setIsFocused] = useState<boolean>(false);
    const shouldFloat = isFocused || admissionNo.length > 0;

    const { error, verify, isVerifying } = useVerifyAdmission({
        onSuccess: (phone) => {
            setMobileNumber(phone);
            setLoginFlowState("enterPassword");
        },
    });

    return (
        <motion.div
            className={styles.loginForm}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
        >
            <div className={styles.loginFormTitle}>
                <h2>Welcome</h2>
                <p>Enter Teacher/Admin User ID to continue</p>
            </div>
            <div className={styles.inputContainer}>
                <div className={styles.inputWrapper}>
                    <label htmlFor="AdmissionNo" className={`${styles.inputLabel} ${shouldFloat ? styles.float : ''} ${error ? styles.errorLabel : ''}`} >Teacher/Admin User ID</label>
                    <input className={`${styles.textInput} ${error ? styles.inputError : ''}`} type="text" id="AdmissionNo" value={admissionNo} onFocus={() => setIsFocused(true)} onBlur={() => setIsFocused(false)} onChange={(e) => setAdmissionNo(e.target.value)} />
                    {error && <p className={styles.errorMessage}>{error}</p>}
                </div>
            </div>
            <div className={styles.buttonsContainer}>
                <button className={styles.primaryButton} disabled={!admissionNo || isVerifying} onClick={() => verify(admissionNo)}>Next</button>
            </div>
            <p className={styles.supportMessage}>Need assistance? Contact our support at 1800-570-6262</p>
        </motion.div>
    )
}

function EnterPassword({
    admissionNo,
    mobileNumber,
    setLoginFlowState,
}: {
    admissionNo: string
    mobileNumber: string
    setLoginFlowState: (flow: LoginFlowState) => void
}) {
    const [password, setPassword] = useState<string>("");
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [isFocused, setIsFocused] = useState<boolean>(false);
    const shouldFloat = isFocused || password.length > 0;

    const { error, loginWithPassword, isLoading } = usePasswordLogin({
        admissionNo,
        mobileNumber,
    });

    return (
        <motion.div
            className={styles.loginForm}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
        >
            <div className={styles.loginFormTitle}>
                <h2>Password</h2>
                <p>{`Please enter the password linked to ${admissionNo}`}</p>
            </div>
            <div className={styles.inputContainer}>
                <div className={styles.inputWrapper}>
                    <div className={styles.prefilledInput}>
                        <span>{admissionNo}</span>
                        <button className={styles.changeButton} onClick={() => setLoginFlowState("enterAdmissionNo")} ><img src={EditIcon} alt="edit" /></button>
                    </div>
                </div>
                <div className={styles.inputWrapper}>
                    <div className={styles.passwordInputWrapper}>
                        <label htmlFor="Password" className={`${styles.inputLabel} ${error ? styles.errorLabel : ''} ${shouldFloat ? styles.float : ''}`} >Password</label>
                        <input className={`${styles.textInput} ${error ? styles.inputError : ''}`} type={showPassword ? "text" : "password"} id="Password" onFocus={() => setIsFocused(true)} onBlur={() => setIsFocused(false)} onChange={(e) => setPassword(e.target.value)} />
                        <button className={styles.togglePasswordButton} onClick={() => setShowPassword(!showPassword)}>
                            <img src={showPassword ? OpenEyeIcon : ClosedEyeIcon} alt={showPassword ? "Hide password" : "Show password"} />
                        </button>
                    </div>
                    {error && <p className={styles.errorMessage}>{error}</p>}
                </div>
            </div>
            <div className={styles.buttonsContainer}>
                <button className={styles.primaryButton} disabled={!password || isLoading} onClick={() => loginWithPassword(password)}>Login</button>
                <button className={styles.secondaryButton} onClick={() => setLoginFlowState("enterOtp")}>Login with OTP</button>
            </div>
        </motion.div>
    )
}

function EnterOtp({
    admissionNo,
    isdCode,
    mobileNumber,
    setLoginFlowState,
}: {
    admissionNo: string
    mobileNumber: string
    isdCode: string
    setLoginFlowState: (flow: LoginFlowState) => void
}) {
    const {
        error,
        setError,
        isVerifying,
        verifyOtp,
        resendOtp,
        captchaRef,
        timeLeft,
        canResend,
        formatTime,
    } = useOtpLogin({
        admissionNo,
        mobileNumber,
        isdCode,
        executeOnMount: true,
    });

    const { otp, isComplete, inputsRef, getOtpString, handleInputChange, handleKeyDown } =
        useOtpInput();

    return (
        <motion.div
            className={styles.loginForm}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
        >
            <div
                ref={captchaRef}
                className="g-recaptcha"
                data-sitekey={environment.captchaSiteKey}
                data-size="invisible"
                style={{
                    position: "fixed",
                    bottom: "20px",
                    right: "20px",
                    zIndex: 9999,
                }}
            />
            <div className={styles.loginFormTitle}>
                <h2>Login with OTP</h2>
                <p>{`Please enter the OTP sent to ${isdCode + '-' + mobileNumber}`}</p>
            </div>
            <div className={styles.inputContainer}>
                <div className={styles.inputWrapper}>
                    <div className={styles.otpInputs}>
                        {[0, 1, 2, 3].map((_, index) => (
                            <input
                                className={`${styles.otpInputText} ${error ? styles.inputError : ''}`}
                                key={index}
                                type="text"
                                maxLength={1}
                                value={otp[index]}
                                ref={(el: HTMLInputElement | null) => {
                                    inputsRef.current[index] = el;
                                }}
                                onChange={(e) => handleInputChange(e, index, () => setError(""))}
                                onKeyDown={(e) => handleKeyDown(e, index)}
                            />
                        ))}
                    </div>
                    {error && <div className={styles.errorMessage} >{error}</div>}
                </div>

            </div>
            <div className={styles.otpInfo}>
                <p className={!canResend ? styles.underline : ''}>Didn’t get the OTP? {canResend ? "Request via " : "Request again in " + formatTime(timeLeft)}</p><span></span>
                <div className={styles.sendOtpButtonContainer}>
                    <button disabled={!canResend} onClick={resendOtp}><img src={SMSIcon} alt="sms" /> SMS</button>
                    <button disabled={!canResend} onClick={resendOtp}><img src={WhatsAppIcon} alt="whatsapp" /> WhatsApp</button>
                </div>
            </div>
            <div className={styles.buttonsContainer}>
                <button className={styles.primaryButton} disabled={!isComplete || isVerifying} onClick={() => verifyOtp(getOtpString())}>Verify OTP</button>
                <button className={styles.secondaryButton} onClick={() => setLoginFlowState("enterPassword")}>Login with Password</button>
            </div>
        </motion.div>
    )
}

function AdmissionNoLogin() {
    const {
        admissionNo,
        setAdmissionNo,
        mobileNumber,
        setMobileNumber,
        isdCode,
        loginFlowState,
        setLoginFlowState,
        goToAdmissionNo,
    } = useLoginFlow();

    const renderContent = () => {
        switch (loginFlowState) {
            case "enterAdmissionNo":
                return <EnterAdmissionNo admissionNo={admissionNo} setAdmissionNo={setAdmissionNo} setMobileNumber={setMobileNumber} setLoginFlowState={setLoginFlowState} />
            case "enterPassword":
                return <EnterPassword admissionNo={admissionNo} mobileNumber={mobileNumber} setLoginFlowState={setLoginFlowState} />
            case "enterOtp":
                return <EnterOtp admissionNo={admissionNo} mobileNumber={mobileNumber} isdCode={isdCode} setLoginFlowState={setLoginFlowState} />
        }
    }
    return (
        <motion.div
            className={styles.mainContainer}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
        >
            <div className={styles.leftContainer}>
                <motion.div
                    className={styles.card}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}
                >
                    <motion.img
                        src={AppLoginLogo}
                        className={styles.loginLogo}
                        alt=""
                        initial={{ opacity: 0, x: "-50%", y: "calc(-50% + 20px)" }}
                        animate={{ opacity: 1, x: "-50%", y: "-50%" }}
                        transition={{ duration: 0.6, ease: "easeOut", delay: 0.4 }}
                    />
                </motion.div>
            </div>
            <div className={styles.rightContainer}>
                {loginFlowState !== "enterAdmissionNo" ? <div className={styles.backButtonContainer} onClick={goToAdmissionNo}><div className={styles.backButton}><img src={LeftArrow} alt="" /></div></div> : null}
                {renderContent()}
            </div>
        </motion.div>
    )
}

export default AdmissionNoLogin
